import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CustomerAuthContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'balmart_customer_token';

function getStoredToken() {
  try { return localStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
}
function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage unavailable (private mode) — rely on cookie only */ }
}
function authHeaders() {
  const t = getStoredToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/customer/me`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      setCustomer(res.data);
      // Refresh the token (sliding session) so localStorage stays in sync
      if (res.data?.access_token) setStoredToken(res.data.access_token);
    } catch {
      setCustomer(null);
      setStoredToken('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/customer/login`, { email, password }, { withCredentials: true });
    if (res.data?.access_token) setStoredToken(res.data.access_token);
    setCustomer(res.data);
    return res.data;
  };

  const register = async (data) => {
    const res = await axios.post(`${API}/customer/register`, data, { withCredentials: true });
    if (res.data?.access_token) setStoredToken(res.data.access_token);
    setCustomer(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/customer/logout`, {}, {
        withCredentials: true,
        headers: authHeaders(),
      });
    } catch { /* ignore */ }
    setStoredToken('');
    setCustomer(null);
  };

  const updateProfile = async (data) => {
    const res = await axios.put(`${API}/customer/profile`, data, {
      withCredentials: true,
      headers: authHeaders(),
    });
    setCustomer(res.data);
    return res.data;
  };

  return (
    <CustomerAuthContext.Provider value={{ customer, loading, login, register, logout, updateProfile, checkAuth }}>
      {children}
    </CustomerAuthContext.Provider>
  );
}

export const useCustomerAuth = () => useContext(CustomerAuthContext);
